<template>
    <div>
        <div class="row gx-3">
            <pc-input
                ref="esModelPickerQuantityInput"
                v-model="quantity"
                v-bind:prepend-items="[quantityAbbreviation]"
                v-bind:validator="quantityValidator"
                v-bind:show-validation-errors="false"
                v-bind:validation-enabled="energyStorageElement.validationEnabled"
                v-bind:class="responsiveClasses.quantityInput"
                v-bind:readonly="readonly"
                v-bind:placeholder="quantityAbbreviation"
                v-bind:data-test-role="testRole.quantity"
                inputmode="numeric"
                lazy
                v-on:validated="onValidated('quantity', $event)" />
            <pc-filtered-select
                v-model="manufacturerId"
                v-bind:validator="manfAndModelValidator"
                v-bind:show-validation-errors="false"
                v-bind:validation-enabled="energyStorageElement.validationEnabled"
                v-bind:alt-label="manfAltLabel"
                v-bind:items="manufacturers"
                v-bind:class="responsiveClasses.manufacturerSelect"
                v-bind:data-test-role="testRole.manufacturer"
                v-bind:readonly="readonly"
                v-bind:display-label="false"
                value-prop="ManufacturerDefinitionId"
                text-prop="ListedName"
                v-on:validated="onValidated('manufacturer', $event)">
            </pc-filtered-select>
            <div v-bind:class="responsiveClasses.modelWrapper">
                <pc-transition transition="slide">
                    <pc-loader
                        v-show="hasManufacturer"
                        v-bind:loading="modelsLoading"
                        small>
                        <pc-filtered-select
                            v-model="modelId"
                            v-bind:validator="manfAndModelValidator"
                            v-bind:show-validation-errors="false"
                            v-bind:validation-enabled="energyStorageElement.validationEnabled"
                            v-bind:alt-label="modelAltLabel"
                            v-bind:items="models"
                            v-bind:data-test-role="testRole.model"
                            v-bind:readonly="readonly"
                            v-bind:display-label="false"
                            value-prop="ModelId"
                            text-prop="Name"
                            v-on:validated="onValidated('model', $event)">
                        </pc-filtered-select>
                    </pc-loader>
                </pc-transition>
            </div>
            <div
                v-if="!readonly && chosenModel"
                class="col-auto">
                <pc-loader
                    small
                    v-bind:loading="copyingComponent">
                    <pc-button-icon
                        v-bind:disabled="copyingComponent"
                        v-bind:link="copyButtonConfig.link"
                        v-bind:style="copyButtonConfig.style"
                        v-bind:icon="copyButtonConfig.iconName"
                        v-on:click="onCopyButtonClicked">
                        {{ componentCloneButtonLabel }}
                    </pc-button-icon>
                </pc-loader>
            </div>
        </div>
        <pc-transition transition="slide">
            <div
                v-if="chosenModel && chosenModelIesPower"
                class="col-12 col-lg-9">
                <strong> {{ chosenModelIesPowerLabel }} </strong>
                <pc-temp-space />
                <span data-test-role="model-iespower">{{ chosenModelIesPower }}</span>
                <pc-temp-space />
                <span>{{ modelPowerUnitKilowatt }}</span>
            </div>
        </pc-transition>
        <pc-transition transition="slide">
            <div
                v-if="chosenModel && chosenModelRating"
                class="col-12 col-lg-9">
                <strong> {{ chosenModelRatingLabel }} </strong>
                <pc-temp-space />
                <span data-test-role="model-display-value">{{ chosenModelRating }}</span>
                <pc-temp-space />
                <span>{{ chosenModelRatingUnit }}</span>
            </div>
        </pc-transition>
        <pc-transition transition="slide">
            <div
                v-if="chosenModel && chosenModelTechnology"
                class="col-12 col-lg-9">
                <strong> {{ chosenModelTechnologyLabel }} </strong>
                <pc-temp-space />
                <span data-test-role="model-technology">{{ chosenModelTechnology }}</span>
            </div>
        </pc-transition>
        <validation-render-helper
            v-bind:validation-error="validationErrors"
            class="mt-0 col-12 col-lg-9" />
    </div>
</template>

<script>
    import { VALIDATOR_TYPES, getValidator } from "Validation/validator";
    import validationRenderHelper from "Validation/_validation-error-helper.vue";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./energystoragemodelpicker.i18n";
    import { COMPONENT_TYPE } from "Types/componentTypes";
    import { convertValueToString } from "Util/util";
    import { getResponsiveClassMap } from "Responsive/manager";

    const responsiveConfig = {
        quantityInput: {
            // mb-3 mb-md-0 col-6 col-sm-3 col-md-2
            col: { xs: 6, sm: 3, md: 2 },
            mb: { xs: 3, md: 0 }
        },
        manufacturerSelect: {
            // mb-3 mb-md-0 col-12 col-sm-9 col-md-3
            col: { xs: 12, sm: 9, md: 3 },
            mb: { xs: 3, md: 0 }
        },
        modelWrapper: {
            // col-12 col-md-4
            col: { xs: 12, md: 4 }
        }
    };

    function mapComputeds(propNames) {
        const props = {};
        propNames.forEach((name) => {
            props[name] = {
                get() {
                    return this.spec[name];
                },
                set(value) {
                    switch (this.equipmentType) {
                        case COMPONENT_TYPE.Integrated_Energy_Storage:
                        case COMPONENT_TYPE.Storage_System_Inverter:
                            this.energyStorageElement.setParentItemProperty({
                                subsystemId: this.spec.id,
                                name,
                                value
                            });
                            break;

                        case COMPONENT_TYPE.Battery:
                            this.energyStorageElement.setEquipmentItemProperty({
                                subsystemId: this.parentId,
                                equipmentItemId: this.spec.id,
                                name,
                                value
                            });
                            break;
                    }
                }
            };
        });
        return props;
    }

    // Indexes within the model.DisplayDetails list
    const displayDetailEquipmentModelRating = 0;
    const displayDetailEquipmentModelBatteryTechnology = 1;
    const displayDetailEquipmentModelIesPower = 2;

    export default {
        components: { validationRenderHelper },
        inject: {
            energyStorageElement: {
                from: "energyStorageElement"
            },
            viewport: {
                from: "viewport",
                default: null
            }
        },
        props: {
            spec: { type: Object, required: true },
            parentId: { type: String, required: false, default: "" },
            manufacturers: { type: Array, required: true },
            loadModels: { type: Function, required: true },
            equipmentType: { type: Number, required: false, default: COMPONENT_TYPE.Unspecified },
            readonly: { type: Boolean, required: false, default: false }
        },
        emits: ["copy-component"],
        data() {
            const quantityValidatorOptions = [
                { type: VALIDATOR_TYPES.required, overrideMsg: "Please fill in quantity." },
                {
                    type: VALIDATOR_TYPES.positiveInteger,
                    overrideMsg: "Quantity must be a whole number greater than 0."
                }
            ];
            const quantityValidator = getValidator(this.$app, quantityValidatorOptions);

            const manfAndModelValidatorOptions = [
                { type: VALIDATOR_TYPES.required, overrideMsg: "Please select manufacturer and model." }
            ];
            const manfAndModelValidator = getValidator(this.$app, manfAndModelValidatorOptions);
            const copyButtonConfig = {
                link: true,
                style: {
                    textDecoration: "none"
                },
                iconName: "copy"
            };
            return {
                models: [],
                modelsLoading: false,
                copyButtonConfig,
                copyingComponent: false,
                quantityValidator,
                manfAndModelValidator,
                validationResults: {
                    quantity: null,
                    manufacturer: null,
                    model: null
                }
            };
        },
        computed: {
            testRole() {
                let baseRole = "";
                switch (this.equipmentType) {
                    case COMPONENT_TYPE.Integrated_Energy_Storage:
                        baseRole = "energystorage-ies";
                        break;
                    case COMPONENT_TYPE.Storage_System_Inverter:
                        baseRole = "energystorage-inverter";
                        break;
                    case COMPONENT_TYPE.Battery:
                        baseRole = "energystorage-battery";
                        break;
                }
                return {
                    quantity: `${baseRole}-quantity`,
                    manufacturer: `${baseRole}-manufacturer-select`,
                    model: `${baseRole}-model-select`
                };
            },
            validationErrors() {
                const validationErrors = [];
                if (this.validationResults.quantity && this.validationResults.quantity.hasError) {
                    validationErrors.push(this.validationResults.quantity.error);
                }

                const manfHasError =
                    this.validationResults.manufacturer && this.validationResults.manufacturer.hasError;

                if (manfHasError) {
                    validationErrors.push(this.validationResults.manufacturer.error);
                }

                if (
                    this.validationResults.model &&
                    this.validationResults.model.hasError &&
                    !manfHasError // don't add duplicate message for missing manf/model
                ) {
                    validationErrors.push(this.validationResults.model.error);
                }

                return validationErrors;
            },
            hasManufacturer() {
                return Boolean(this.spec.manufacturerId);
            },
            selectedManufacturerName() {
                let manufName = "";
                const manufId = this.manufacturerId;
                const manuf = this.manufacturers.find(function (m) {
                    return m.ManufacturerDefinitionId === manufId;
                });
                if (manuf) {
                    manufName = manuf.ListedName;
                }
                return manufName;
            },
            chosenModel() {
                if (this.spec.modelId && this.models.length > 0) {
                    const model = this.models.find((m) => m.ModelId.toString() == this.spec.modelId);
                    return model;
                }
                return null;
            },
            chosenModelRatingLabel() {
                // This is the label for the "primary" rating (in the sense of the first one we defined) for the component,
                //  so what it is labeling varies by component
                let label = "";
                switch (this.equipmentType) {
                    case COMPONENT_TYPE.Integrated_Energy_Storage:
                        label = this.iesEnergyRatingLabel;
                        break;
                    case COMPONENT_TYPE.Storage_System_Inverter:
                        label = this.inverterRatingLabel;
                        break;
                    case COMPONENT_TYPE.Battery:
                        label = this.batteryRatingLabel;
                        break;
                }
                return label;
            },
            chosenModelRatingUnit() {
                let unit = "";
                switch (this.equipmentType) {
                    case COMPONENT_TYPE.Integrated_Energy_Storage:
                        unit = this.modelPowerUnitKilowattHour;
                        break;
                    case COMPONENT_TYPE.Storage_System_Inverter:
                        unit = this.modelPowerUnitKilowatt;
                        break;
                    case COMPONENT_TYPE.Battery:
                        unit = this.modelPowerUnitKilowattHour;
                        break;
                }

                return unit;
            },
            chosenModelIesPowerLabel() {
                let label = "";
                switch (this.equipmentType) {
                    case COMPONENT_TYPE.Integrated_Energy_Storage:
                        label = this.iesPowerRatingLabel;
                        break;
                }
                return label;
            },
            chosenModelTechnologyLabel() {
                let label = "";
                switch (this.equipmentType) {
                    case COMPONENT_TYPE.Integrated_Energy_Storage:
                    case COMPONENT_TYPE.Battery:
                        label = this.batteryTechnologyLabel;
                        break;
                }
                return label;
            },
            chosenModelRating() {
                let rating = "";
                if (
                    this.chosenModel &&
                    this.chosenModel.DisplayDetails &&
                    this.chosenModel.DisplayDetails.length > displayDetailEquipmentModelRating
                ) {
                    rating = this.chosenModel.DisplayDetails[displayDetailEquipmentModelRating];
                    if (convertValueToString(rating).trim() && isFinite(rating)) {
                        let digits = 0;
                        switch (this.equipmentType) {
                            case COMPONENT_TYPE.Integrated_Energy_Storage:
                            case COMPONENT_TYPE.Battery:
                                digits = 2;
                                break;
                            case COMPONENT_TYPE.Storage_System_Inverter:
                                digits = 3;
                                break;
                        }
                        rating = Number(rating).toFixed(digits);
                    }
                }
                return rating;
            },
            chosenModelIesPower() {
                let iesPower = "";
                if (
                    this.chosenModel &&
                    this.chosenModel.DisplayDetails &&
                    this.chosenModel.DisplayDetails.length > displayDetailEquipmentModelIesPower
                ) {
                    iesPower = this.chosenModel.DisplayDetails[displayDetailEquipmentModelIesPower];
                    if (convertValueToString(iesPower).trim() && isFinite(iesPower)) {
                        let digits = 0;
                        switch (this.equipmentType) {
                            case COMPONENT_TYPE.Integrated_Energy_Storage:
                            case COMPONENT_TYPE.Battery:
                                digits = 2;
                                break;
                            case COMPONENT_TYPE.Storage_System_Inverter:
                                digits = 3;
                                break;
                        }
                        iesPower = Number(iesPower).toFixed(digits);
                    }
                }
                return iesPower;
            },
            chosenModelTechnology() {
                let technology = "";
                if (
                    this.chosenModel &&
                    this.chosenModel.DisplayDetails &&
                    this.chosenModel.DisplayDetails.length > displayDetailEquipmentModelBatteryTechnology
                ) {
                    technology = this.chosenModel.DisplayDetails[displayDetailEquipmentModelBatteryTechnology];
                }
                return technology;
            },
            ...mapComputeds([
                "quantity",
                "manufacturerId",
                "modelId",
                "energyRating",
                "powerRating",
                "equipmentTechnology"
            ]),
            responsiveClasses() {
                return getResponsiveClassMap(this.viewport, responsiveConfig);
            },
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            )
        },
        watch: {
            modelId() {
                if (this.chosenModel && this.chosenModel.ManufacturerId != this.manufacturerId) {
                    this.$app.showErrorDialog(
                        this.manufModelMismatchDialogTitle,
                        this.manufModelMismatchDialogText,
                        true
                    );
                    this.modelId = "";
                    this.getModels();
                }

                if (this.chosenModel) {
                    if (this.chosenModel.energyRating && this.chosenModel.energyRating != this.energyRating) {
                        this.energyRating = this.chosenModel.energyRating;
                    }
                    if (this.chosenModel.powerRating && this.chosenModel.powerRating != this.powerRating) {
                        this.powerRating = this.chosenModel.powerRating;
                    }
                    if (this.chosenModel.Technology && this.chosenModel.Technology != this.equipmentTechnology) {
                        this.equipmentTechnology = this.chosenModel.Technology;
                    }
                } else {
                    if (this.energyRating) {
                        this.energyRating = "";
                    }
                    if (this.powerRating) {
                        this.powerRating = "";
                    }
                    if (this.equipmentTechnology) {
                        this.equipmentTechnology = "";
                    }
                }
            },
            manufacturerId() {
                switch (this.equipmentType) {
                    case COMPONENT_TYPE.Integrated_Energy_Storage:
                    case COMPONENT_TYPE.Storage_System_Inverter:
                        this.energyStorageElement.setParentItemProperty({
                            subsystemId: this.spec.id,
                            name: "modelId",
                            value: ""
                        });
                        break;

                    case COMPONENT_TYPE.Battery:
                        this.energyStorageElement.setEquipmentItemProperty({
                            subsystemId: this.parentId,
                            equipmentItemId: this.spec.id,
                            name: "modelId",
                            value: ""
                        });
                        break;
                }
                this.getModels();
            }
        },
        created() {
            this.getModels();
        },
        methods: {
            onValidated(prop, validationResult) {
                this.validationResults[prop] = validationResult;
            },
            async getModels() {
                if (this.hasManufacturer) {
                    try {
                        this.modelsLoading = true;
                        // Give the UI a chance to display the spinner for the model pc-loader
                        await this.$app.timeout(0);
                        this.models = await this.loadModels(this.equipmentType, this.selectedManufacturerName);
                    } finally {
                        this.modelsLoading = false;
                    }
                }
            },
            async onCopyButtonClicked() {
                this.copyingComponent = true;
                await this.$app.timeout(0);
                await this.$emit("copy-component");
                this.copyingComponent = false;
            },
            setFocusToQuantityInput() {
                this.$refs.esModelPickerQuantityInput.focusIn();
            }
        }
    };
</script>
